/**
 * Shared API Types
 * Single source of truth for API request/response structures
 *
 * These types are shared between frontend and backend to ensure type safety
 * across the API boundary.
 */

import type { ReportDateMode } from './reportMode';
import type { RSVPScaleRatios, RSVPSizeCode, RSVPSizeCounts } from './units';

// Re-export Prisma types for backend use only
export type { Member as PrismaMember, PickupLocation as PrismaPickupLocation, Menu as PrismaMenu, RSVP as PrismaRSVP } from '../../generated/prisma/client';

// =============================================================================
// API DTOs (Data Transfer Objects)
// These match the actual wire format with BigInt→string and Date→ISO string
// =============================================================================

/**
 * Member as returned by API (wire format)
 * BigInt fields are serialized as strings
 * Date fields are serialized as ISO strings
 */
export interface Member {
  id: string;  // BigInt → string
  email: string | null;
  name: string;
  phone: string | null;
  thaaliNumber: string | null;
  pickupLocationId: number | null;
  defaultSize: string | null;
  autoRsvpWithDefault: boolean;
  mithaas: boolean;
  status: 'PENDING' | 'ACTIVE' | 'INACTIVE';
  role: 'MEMBER' | 'ADMIN' | 'CATERER';
  createdAt: string;  // Date → ISO string
  lastLogin: string | null;  // Date → ISO string
  approvedAt: string | null;  // Date → ISO string
  approvedBy: string | null;  // BigInt → string
  pickupLocation?: PickupLocation;  // Optional relation
}

/**
 * PickupLocation as returned by API (wire format)
 * Date fields are serialized as ISO strings
 */
export interface PickupLocation {
  id: number;
  name: string;
  address: string | null;
  colorCode: string;
  isActive: boolean;
  createdAt?: string;  // Date → ISO string
  updatedAt?: string;  // Date → ISO string
}

// =============================================================================
// Member Management API Types
// =============================================================================

/**
 * Filters for fetching members list
 */
export interface MemberFilters {
  search?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'ALL';  // Note: PENDING excluded (shown separately)
  role?: 'MEMBER' | 'ADMIN' | 'CATERER';
  pickupLocationId?: number;
  page?: number;
  pageSize?: number;
}

/**
 * Response from GET /api/admin/members/pending
 */
export interface PendingMembersResponse {
  members: Member[];
  count: number;
}

/**
 * Response from GET /api/admin/members
 */
export interface MembersResponse {
  members: Member[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Request body for PUT /api/admin/members/:id
 * Can be used for both approval and editing
 * Note: approvedAt and approvedBy are set automatically by backend
 */
export interface UpdateMemberRequest {
  // Profile fields
  name?: string;
  email?: string;
  phone?: string;
  thaaliNumber?: string;
  pickupLocationId?: number;
  defaultSize?: RSVPSizeCode | null;
  autoRsvpWithDefault?: boolean;
  mithaas?: boolean;

  // Role and status
  role?: 'MEMBER' | 'ADMIN' | 'CATERER';
  status?: 'PENDING' | 'ACTIVE' | 'INACTIVE';
}

/**
 * Request body for POST /api/admin/members/:id/reject
 */
export interface RejectMemberRequest {
  reason?: string;
}

/**
 * Response from POST /api/admin/members/:id/reject
 */
export interface RejectMemberResponse {
  message: string;
}

/**
 * Request body for POST /api/admin/members/:id/reset-password
 */
export interface ResetMemberPasswordRequest {
  newPassword: string;
}

// =============================================================================
// Pickup Location API Types
// =============================================================================

/**
 * Request body for POST /api/pickup-locations
 */
export interface CreatePickupLocationRequest {
  name: string;
  address?: string | null;
  colorCode?: string;
  isActive?: boolean;
}

/**
 * Request body for PUT /api/pickup-locations/:id
 */
export interface UpdatePickupLocationRequest {
  name?: string;
  address?: string | null;
  colorCode?: string;
  isActive?: boolean;
}

/**
 * Response from POST /api/pickup-locations and PUT /api/pickup-locations/:id
 */
export interface PickupLocationResponse {
  location: PickupLocation;
}

/**
 * Response from GET /api/pickup-locations
 */
export interface PickupLocationsResponse {
  locations: PickupLocation[];
}

// =============================================================================
// Authentication API Types
// =============================================================================

/**
 * Request body for POST /api/auth/login
 */
export interface LoginRequest {
  itsId: string;
  password: string;
}

/**
 * Response from POST /api/auth/login
 */
export interface LoginResponse {
  user: AuthUser;
}

/**
 * Request body for POST /api/auth/register
 */
export interface RegisterRequest {
  itsId: string;
  password: string;
  name: string;
  email?: string;
  phone?: string;
  pickupLocationId?: number;
  defaultSize?: RSVPSizeCode;
}

/**
 * Response from GET /api/auth/me
 */
export interface MeResponse {
  user: AuthUser;
}

/**
 * Generic message response.
 */
export interface MessageResponse {
  message: string;
}

/**
 * Request body for PATCH /api/auth/me/password
 */
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * Request body for PATCH /api/auth/me/preferences
 * Members can update their own preferences (mithaas, etc.)
 */
export interface UpdatePreferencesRequest {
  defaultSize?: RSVPSizeCode | null;
  mithaas?: boolean;
  autoRsvpWithDefault?: boolean;
}

/**
 * User info returned by GET /api/auth/me and POST /api/auth/login
 */
export interface AuthUser {
  id: string;
  name: string;
  email: string | null;
  role: 'MEMBER' | 'ADMIN' | 'CATERER';
  status: 'PENDING' | 'ACTIVE' | 'INACTIVE';
  pickupLocationId: number | null;
  pickupLocationName?: string | null;
  pickupLocationColorCode?: string | null;
  thaaliNumber?: string | null;
  defaultSize?: string | null;
  autoRsvpWithDefault?: boolean;
  phone?: string | null;
  mithaas?: boolean;
}

// =============================================================================
// Menu Management API Types
// =============================================================================

/**
 * Basic recipe info for menu linking
 * Lightweight type used in menu responses (no ingredients)
 */
export interface RecipeBasic {
  id: number;
  name: string;
  category: RecipeCategory;
}

/**
 * Menu-recipe link as returned by API
 * Represents a junction between a menu and a recipe
 */
export interface MenuRecipeLink {
  menuRecipeId: number;
  recipeId: number;
  name: string;
  category: RecipeCategory;
  isOptional: boolean;
}

export type MenuRSVPAudience = 'FMB_ONLY' | 'JAMAAT_WIDE';
export type ParticipantType = 'MEMBER' | 'PUBLIC';

/**
 * Menu as returned by API (wire format)
 * Date fields are serialized as ISO strings
 */
export interface Menu {
  id: number;
  date: string;  // Date → ISO string (YYYY-MM-DD format)
  cookingDate: string | null;  // Date menu is cooked (YYYY-MM-DD), null for announcements
  title: string;
  description: string | null;
  type: 'MENU' | 'ANNOUNCEMENT';
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  rsvpAudience?: MenuRSVPAudience;
  publicRsvpSlug?: string | null;
  rsvpCutoffAt?: string | null;
  recipes: MenuRecipeLink[];  // Linked recipes (via junction table)
  publishedAt: string | null;  // Date → ISO string
  createdById: string;  // BigInt → string
  assignedCatererId: string | null;  // BigInt → string
  assignedCatererNameSnapshot: string | null;
  createdAt: string;  // Date → ISO string
  updatedAt: string;  // Date → ISO string
  createdBy?: Member;  // Optional relation
  rsvpCountS?: number;  // Small size RSVP count (for list views)
  rsvpCountM?: number;  // Medium size RSVP count (for list views)
  rsvpCountL?: number;  // Large size RSVP count (for list views)
  memberRsvpCountS?: number;
  memberRsvpCountM?: number;
  memberRsvpCountL?: number;
  publicRsvpCountS?: number;
  publicRsvpCountM?: number;
  publicRsvpCountL?: number;
  rsvpCutoffPassed?: boolean;  // True if RSVP cutoff has passed for this menu's week
}

/**
 * Recipe link entry for create/update requests
 */
export interface MenuRecipeLinkInput {
  menuRecipeId?: number;  // Omit for new links, include for existing
  recipeId: number;
  isOptional?: boolean;
}

/**
 * Request body for POST /api/menus
 */
export interface CreateMenuRequest {
  date: string;  // ISO date string (YYYY-MM-DD)
  title: string;
  description?: string;
  type: 'MENU' | 'ANNOUNCEMENT';
  recipes?: MenuRecipeLinkInput[];  // Recipe links (only for MENU type)
  cookingDate?: string | null;  // Required for MENU, null for ANNOUNCEMENT
  assignedCatererId?: string | null;  // Caterer ITS ID assignment
  rsvpAudience?: MenuRSVPAudience;
  rsvpCutoffAt?: string | null;
}

/**
 * Request body for PUT /api/menus/:id
 */
export interface UpdateMenuRequest {
  title?: string;
  description?: string;
  type?: 'MENU' | 'ANNOUNCEMENT';
  status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  recipes?: MenuRecipeLinkInput[];  // Recipe links (omit to leave unchanged)
  cookingDate?: string | null;  // Required when resulting type is MENU
  assignedCatererId?: string | null;  // Caterer ITS ID assignment
  rsvpAudience?: MenuRSVPAudience;
  rsvpCutoffAt?: string | null;
}

/**
 * Response from GET /api/menus
 */
export interface MenusResponse {
  menus: Menu[];
}

/**
 * Response from POST /api/menus, PUT /api/menus/:id, and publish/unpublish
 */
export interface MenuResponse {
  menu: Menu;
  /** Number of members auto-RSVP'd on publish (present only on the publish response) */
  autoRsvpCount?: number;
  /** True when the best-effort auto-RSVP step failed after publish (publish response only) */
  autoRsvpFailed?: boolean;
}

// =============================================================================
// System Settings API Types
// =============================================================================

export interface SystemSettings {
  cutoffDay: number;
  cutoffTime: string;  // HH:MM 24-hour format
}

export interface SystemSettingsResponse {
  cutoff: SystemSettings;
}

export interface UpdateSystemSettingsRequest {
  cutoffDay?: number;
  cutoffTime?: string;
}

// =============================================================================
// Vendor API Types
// =============================================================================

export interface Vendor {
  id: number;
  name: string;
  isActive: boolean;
  notes: string | null;
  ingredientCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface VendorsResponse {
  vendors: Vendor[];
}

export interface VendorResponse {
  vendor: Vendor;
}

export interface DeleteVendorResponse {
  message: string;
  vendor: Vendor;
}

export interface VendorFilters {
  search?: string;
  isActive?: boolean;
}

export interface CreateVendorRequest {
  name: string;
  notes?: string | null;
  isActive?: boolean;
}

export interface UpdateVendorRequest {
  name?: string;
  notes?: string | null;
  isActive?: boolean;
}

// =============================================================================
// RSVP API Types
// =============================================================================

/**
 * RSVP as returned by API (wire format)
 * BigInt fields are serialized as strings
 * Date fields are serialized as ISO strings
 */
export interface RSVP {
  id: number;
  memberId: string;  // BigInt → string
  menuId: number;
  size: RSVPSizeCode;
  status: 'ACTIVE' | 'CANCELLED';
  optedOutMenuRecipeIds: number[];  // MenuRecipe IDs opted out of (always present, default [])
  createdAt: string;  // Date → ISO string
  updatedAt: string;  // Date → ISO string
  modifiedById: string | null;  // BigInt → string
  menu?: Menu;  // Optional relation
  member?: Member;  // Optional relation
}

/**
 * Request body for POST /api/rsvps
 */
export interface CreateRSVPRequest {
  menuId: number;
  size: RSVPSizeCode;
  optedOutMenuRecipeIds?: number[];
}

/**
 * Request body for PUT /api/rsvps/:id
 */
export interface UpdateRSVPRequest {
  size?: RSVPSizeCode;
  status?: 'ACTIVE' | 'CANCELLED';
  optedOutMenuRecipeIds?: number[];
}

/**
 * Request body for POST /api/admin/members/:id/rsvps/override
 */
export interface AdminRSVPOverrideRequest {
  menuId: number;
  size: RSVPSizeCode | 'SKIP';
  optedOutMenuRecipeIds?: number[];
}

/**
 * Response from POST /api/admin/members/:id/rsvps/override
 */
export interface AdminRSVPOverrideResponse {
  rsvp: RSVP | null;
}

/**
 * Response from GET /api/rsvps
 */
export interface RSVPsResponse {
  rsvps: RSVP[];
}

/**
 * Response from POST /api/rsvps and PUT /api/rsvps/:id
 */
export interface RSVPResponse {
  rsvp: RSVP;
}

// =============================================================================
// Public RSVP API Types
// =============================================================================

export interface PublicRSVP {
  id: number;
  menuId: number;
  itsId: string;
  name: string;
  phone: string;
  pickupLocationId: number;
  pickupLocation?: Pick<PickupLocation, 'id' | 'name' | 'colorCode'>;
  size: RSVPSizeCode;
  mithaas: boolean;
  status: 'ACTIVE' | 'CANCELLED';
  optedOutMenuRecipeIds: number[];
  createdAt: string | null;
  updatedAt: string | null;
  menu?: Menu;
}

export interface PublicRSVPEventResponse {
  menu: Menu;
  canRsvp: boolean;
  unavailableReason: string | null;
  cutoffTimezone: string;
}

export type PublicRSVPEligibilityResponse =
  | { status: 'PUBLIC_ALLOWED'; itsId: string }
  | { status: 'ACTIVE_MEMBER_EXISTS'; loginPath: string; rsvpPath: string };

export interface PublicRSVPRequest {
  itsId: string;
  name: string;
  phone: string;
  pickupLocationId: number;
  size: RSVPSizeCode;
  mithaas: boolean;
  optedOutMenuRecipeIds?: number[];
}

export type PublicRSVPUpdateRequest = Omit<PublicRSVPRequest, 'itsId'>;

export interface PublicRSVPSubmitResponse {
  rsvp: PublicRSVP;
  editToken: string;
  editPath: string;
}

export interface PublicRSVPLookupResponse {
  editToken: string;
  editPath: string;
}

export interface PublicRSVPManageResponse {
  rsvp: PublicRSVP;
  canEdit: boolean;
  cutoffTimezone: string;
}

/**
 * RSVP with optional feedback (for member views with includeFeedback=true)
 */
export interface RSVPWithFeedback extends RSVP {
  feedback?: string | null;  // Member's feedback for this menu
}

/**
 * Response from GET /api/rsvps with includeFeedback=true
 */
export interface RSVPsWithFeedbackResponse {
  rsvps: RSVPWithFeedback[];
}

// =============================================================================
// Bulk RSVP API Types
// =============================================================================

/**
 * Feedback item for bulk RSVP submission
 */
export interface FeedbackItem {
  menuId: number;
  feedbackText: string;  // Empty string means delete existing feedback
}

/**
 * Request body for POST /api/rsvps/bulk
 */
export interface BulkRSVPRequest {
  selections: Array<{ menuId: number; size: RSVPSizeCode; optedOutMenuRecipeIds?: number[] }>;
  feedback: FeedbackItem[];
  scopeMenuIds?: number[];
}

/**
 * Response from POST /api/rsvps/bulk
 */
export interface BulkRSVPResponse {
  created: RSVP[];
  updated: RSVP[];
  deleted: number[];  // RSVP IDs that were cancelled
  skippedSelections?: number[];  // Menu IDs skipped due to cutoff
  cutoffTimezone?: string;  // Timezone used for cutoff evaluation
  feedbackUpserted: number;  // Count of feedback entries created/updated
  feedbackDeleted: number;  // Count of feedback entries removed
}

// =============================================================================
// Feedback API Types
// =============================================================================

/**
 * Feedback as returned by API (wire format)
 * BigInt fields are serialized as strings
 * Date fields are serialized as ISO strings
 */
export interface Feedback {
  id: number;
  memberId: string | null;  // Hidden from non-admin viewers
  menuId: number;
  feedbackText: string;
  createdAt: string;  // Date → ISO string
  updatedAt: string;  // Date → ISO string
  member?: {
    id: string;
    name: string;
  };
  menu?: {
    id: number;
    date: string;
    title: string;
    catererName: string | null;
  };
}

/**
 * Grouped feedback by date for admin view
 */
export interface FeedbackByDate {
  date: string;  // YYYY-MM-DD
  menus: Array<{
    menuId: number;
    menuTitle: string;
    catererName: string | null;
    feedback: Array<{
      id: number;
      memberName: string;
      feedbackText: string;
    }>;
    averageRating: number | null;  // 1-5, null when the menu has no ratings
    ratingCount: number;
  }>;
}

export interface MenuRatingSummary {
  averageRating: number | null;  // 1-5, null when the menu has no ratings
  ratingCount: number;
  currentUserRating: number | null;  // 1-5, null when the member hasn't rated
}

/**
 * Response from GET /api/feedback (admin/caterer view)
 */
export interface FeedbackListResponse {
  feedback: Feedback[];  // Flat list
  grouped: FeedbackByDate[];  // Grouped by date
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// =============================================================================
// RSVP Reports API Types
// =============================================================================

/**
 * Individual RSVP detail item for reports
 */
export interface RSVPReportItem {
  date: string;  // YYYY-MM-DD
  menuTitle: string;
  menuId: number;
  participantType: ParticipantType;
  memberId: string;
  memberName: string;
  phone: string | null;
  thaaliNumber: string | null;
  pickupLocationId: number | null;
  pickupLocationName: string | null;
  pickupLocationColorCode?: string | null;
  size: RSVPSizeCode;
  mithaas: boolean;
  optionalChoices: { menuRecipeId: number; recipeName: string; optedOut: boolean }[];
}

/**
 * Matched service-day menu for a cooking-day report
 */
export interface CookingDayMenuItem {
  menuId: number;
  serviceDate: string;  // YYYY-MM-DD
  menuTitle: string;
  optionalRecipes: { menuRecipeId: number; recipeName: string }[];
}

/**
 * Member selection aligned to a matched service-day menu
 */
export interface CookingDaySelectionItem {
  menuId: number;
  size: RSVPSizeCode | null;
  optedOutMenuRecipeIds: number[];
}

/**
 * Union row for a cooking-day report
 */
export interface CookingDayReportRow {
  participantType: ParticipantType;
  memberId: string;
  memberName: string;
  phone: string | null;
  thaaliNumber: string | null;
  pickupLocationId: number | null;
  pickupLocationName: string | null;
  pickupLocationColorCode?: string | null;
  mithaas: boolean;
  selections: CookingDaySelectionItem[];
}

/**
 * Per-location total count for displayed cooking-day rows
 */
export interface CookingDayLocationTotalItem {
  locationId: number | null;
  locationName: string;
  locationColorCode?: string | null;
  totalCount: number;
}

/**
 * Cooking-day report payload
 */
export interface CookingDayReportData {
  cookingDate: string;  // YYYY-MM-DD
  menus: CookingDayMenuItem[];
  locationTotals: CookingDayLocationTotalItem[];
  rows: CookingDayReportRow[];
}

/**
 * Summary item - aggregated counts per date/menu
 */
export interface RSVPSummaryItem {
  date: string;  // YYYY-MM-DD
  menuTitle: string;
  menuId: number;
  totalRsvps: number;
  smallCount: number;
  mediumCount: number;
  largeCount: number;
}

/**
 * Location breakdown item - aggregated counts per date/menu/location
 */
export interface RSVPLocationBreakdownItem {
  date: string;  // YYYY-MM-DD
  menuTitle: string;
  menuId: number;
  locationId: number | null;
  locationName: string;
  locationColorCode?: string | null;
  totalRsvps: number;
  smallCount: number;
  mediumCount: number;
  largeCount: number;
}

/**
 * Report metadata
 */
export interface RSVPReportMeta {
  startDate: string;  // YYYY-MM-DD
  endDate: string;    // YYYY-MM-DD
  locationFilter: number | null;
  totalRsvps: number;
  totalSmall: number;
  totalMedium: number;
  totalLarge: number;
}

/**
 * Response from GET /api/admin/reports/rsvps
 */
export interface RSVPReportResponse {
  summary: RSVPSummaryItem[];
  byLocation: RSVPLocationBreakdownItem[];
  details: RSVPReportItem[];
  meta: RSVPReportMeta;
  cookingDay?: CookingDayReportData | null;
}

/**
 * Query parameters for RSVP reports
 */
export interface RSVPReportParams {
  start: string;  // YYYY-MM-DD
  end: string;    // YYYY-MM-DD
  mode?: ReportDateMode;
  locationId?: number;
  includeDetails?: boolean;
}

// =============================================================================
// Recipe API Types
// =============================================================================

/**
 * Recipe category enum (matches Prisma RecipeCategory)
 */
export type RecipeCategory = 'DAAL_ITEM' | 'CHICKEN_ITEM' | 'VEG_ITEM' | 'GOSHT_ITEM';

/**
 * All recipe categories - single source of truth for frontend
 * Use this instead of hardcoding category arrays
 */
export const RECIPE_CATEGORIES: RecipeCategory[] = ['DAAL_ITEM', 'CHICKEN_ITEM', 'VEG_ITEM', 'GOSHT_ITEM'];

/**
 * Human-readable labels for recipe categories
 */
export const RECIPE_CATEGORY_LABELS: Record<RecipeCategory, string> = {
  DAAL_ITEM: 'Daal Item',
  CHICKEN_ITEM: 'Chicken Item',
  VEG_ITEM: 'Veg Item',
  GOSHT_ITEM: 'Gosht Item',
};

/**
 * Recipe as returned by API (wire format)
 * BigInt fields are serialized as strings
 * Date fields are serialized as ISO strings
 */
export interface Recipe {
  id: number;
  name: string;
  category: RecipeCategory;
  description: string | null;
  prepNotes: string | null;
  smallToLargeRatioOverride: string | null;  // Decimal → string (null uses system default)
  mediumToLargeRatioOverride: string | null;  // Decimal → string (null uses system default)
  isActive: boolean;
  ingredientCount?: number;
  createdById: string;  // BigInt → string
  updatedById: string | null;  // BigInt → string
  createdAt: string;  // Date → ISO string
  updatedAt: string;  // Date → ISO string
}

/**
 * Request body for POST /api/recipes
 */
export interface CreateRecipeRequest {
  name: string;
  category: RecipeCategory;
  description?: string | null;
  prepNotes?: string | null;
  smallToLargeRatioOverride?: number | null;
  mediumToLargeRatioOverride?: number | null;
}

/**
 * Request body for PUT /api/recipes/:id
 */
export interface UpdateRecipeRequest {
  name?: string;
  category?: RecipeCategory;
  description?: string | null;
  prepNotes?: string | null;
  smallToLargeRatioOverride?: number | null;
  mediumToLargeRatioOverride?: number | null;
  isActive?: boolean;
}

/**
 * Request body for POST /api/recipes/:id/copy
 */
export interface CopyRecipeRequest {
  name: string;
  category?: RecipeCategory;
  description?: string | null;
  prepNotes?: string | null;
  smallToLargeRatioOverride?: number | null;
  mediumToLargeRatioOverride?: number | null;
  isActive?: boolean;
}

/**
 * Response from GET /api/recipes
 */
export interface RecipesResponse {
  recipes: Recipe[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Response from POST /api/recipes and PUT /api/recipes/:id
 */
export interface RecipeResponse {
  recipe: Recipe;
}

/**
 * Query parameters for GET /api/recipes
 */
export interface RecipeFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  category?: RecipeCategory;
  activeOnly?: boolean;
  includeIngredientCount?: boolean;
}

// =============================================================================
// Ingredient API Types
// =============================================================================

/**
 * Ingredient category enum (matches Prisma IngredientCategory)
 */
export type IngredientCategory = 'PRODUCE' | 'MEAT' | 'SEAFOOD' | 'DAIRY' | 'GRAINS' | 'SPICES' | 'OILS' | 'CANNED' | 'FROZEN' | 'BEVERAGES' | 'OTHER';
export type IngredientSortOption = 'name' | 'category' | 'unit';

/**
 * All ingredient categories - single source of truth for frontend
 * Use this instead of hardcoding category arrays
 */
export const INGREDIENT_CATEGORIES: IngredientCategory[] = [
  'PRODUCE',
  'MEAT',
  'SEAFOOD',
  'DAIRY',
  'GRAINS',
  'SPICES',
  'OILS',
  'CANNED',
  'FROZEN',
  'BEVERAGES',
  'OTHER',
];

/**
 * Human-readable labels for ingredient categories
 */
export const INGREDIENT_CATEGORY_LABELS: Record<IngredientCategory, string> = {
  PRODUCE: 'Produce',
  MEAT: 'Meat',
  SEAFOOD: 'Seafood',
  DAIRY: 'Dairy',
  GRAINS: 'Grains',
  SPICES: 'Spices',
  OILS: 'Oils',
  CANNED: 'Canned',
  FROZEN: 'Frozen',
  BEVERAGES: 'Beverages',
  OTHER: 'Other',
};

/**
 * Ingredient as returned by API (wire format)
 * BigInt fields are serialized as strings
 * Date fields are serialized as ISO strings
 */
export interface Ingredient {
  id: number;
  name: string;
  category: IngredientCategory;
  defaultUnit: string;
  notes: string | null;
  imageUrls: string[];
  isActive: boolean;
  /** Weekly staple: added to every procurement list regardless of RSVPs */
  isStaple: boolean;
  /** Target stock per week in defaultUnit (Decimal -> string); null unless isStaple */
  stapleTargetQuantity: string | null;
  /** Smallest purchasable amount in defaultUnit (Decimal -> string); purchase quantities round up to a multiple. 1 = whole units */
  orderIncrement: string;
  vendorCount?: number;
  preferredVendorName?: string | null;
  recipeUsages?: IngredientRecipeUsage[];
  createdById: string;  // BigInt → string
  updatedById: string | null;  // BigInt → string
  createdAt: string;  // Date → ISO string
  updatedAt: string;  // Date → ISO string
}

/**
 * Recipe usage of an ingredient (returned by GET /api/ingredients/:id?includeRecipeUsages=true)
 */
export interface IngredientRecipeUsage {
  recipeId: number;
  recipeName: string;
  quantityPerLarge: string | null;
  unit: string;
}

/**
 * Response from GET /api/ingredients
 */
export interface IngredientsResponse {
  ingredients: Ingredient[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Query parameters for GET /api/ingredients
 */
export interface IngredientFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  category?: IngredientCategory;
  activeOnly?: boolean | 'all';
  sortBy?: IngredientSortOption;
  includeVendorInfo?: boolean;
  stapleOnly?: boolean;
}

/**
 * Request body for POST /api/ingredients
 */
export interface CreateIngredientRequest {
  name: string;
  category: IngredientCategory;
  defaultUnit: string;
  notes?: string | null;
  imageUrls?: string[];
  isStaple?: boolean;
  stapleTargetQuantity?: number | null;
  orderIncrement?: number;
}

/**
 * GET /api/ingredients/unit-suggestion?name=...|ingredientId=...
 * The unit an ingredient is normally used in, from its usage history (see
 * suggestUnitFromHistory in units.ts), the matched (possibly retired) ingredient,
 * all-time unit counts over its recipes, and the name-based herb rule.
 */
export interface UnitSuggestionResponse {
  suggestion: { unit: string; used: number; of: number } | null;
  byName: string | null;
  matchedIngredient: { id: number; name: string; defaultUnit: string; isActive: boolean } | null;
  allTimeUnits: Array<{ unit: string; count: number }>;
}

/**
 * Response from POST /api/ingredients
 */
export interface IngredientResponse {
  ingredient: Ingredient;
}

// =============================================================================
// Recipe Ingredient (Junction) API Types
// =============================================================================

/**
 * RecipeIngredient as returned by API (wire format)
 * Links an ingredient to a recipe with optional quantity
 */
export interface RecipeIngredient {
  id: number;
  recipeId: number;
  ingredientId: number;
  quantityPerLarge: string | null;  // Decimal → string (nullable for optional quantities)
  unit: string;
  notes: string | null;
  createdAt: string;  // Date → ISO string
  updatedAt: string;  // Date → ISO string
  ingredient?: Ingredient;  // Optional nested ingredient data
}

/**
 * Recipe with ingredients included (extended response)
 */
export interface RecipeWithIngredients extends Recipe {
  ingredients: RecipeIngredient[];
}

/**
 * Request body for POST /api/recipes/:id/ingredients
 */
export interface AddRecipeIngredientRequest {
  ingredientId: number;
  quantityPerLarge?: number | null;  // Optional
  notes?: string | null;
}

/**
 * Request body for PUT /api/recipes/:id/ingredients/:ingredientId
 */
export interface UpdateRecipeIngredientRequest {
  quantityPerLarge?: number | null;
  notes?: string | null;
}

/**
 * Response from GET /api/recipes/:id/ingredients
 */
export interface RecipeIngredientsResponse {
  ingredients: RecipeIngredient[];
}

/**
 * Response from POST /api/recipes/:id/ingredients and PUT /api/recipes/:id/ingredients/:ingredientId
 */
export interface RecipeIngredientResponse {
  recipeIngredient: RecipeIngredient;
}

// =============================================================================
// Ingredient-Vendor Association API Types
// =============================================================================

/**
 * IngredientVendor association as returned by API (wire format)
 * Links an ingredient to a vendor with optional preferred flag
 */
export interface IngredientVendor {
  id: number;
  ingredientId: number;
  vendorId: number;
  isPreferred: boolean;
  createdAt: string;  // Date → ISO string
  updatedAt: string;  // Date → ISO string
  vendor?: Vendor;    // Optional nested vendor data
  ingredient?: Ingredient;  // Optional nested ingredient data
}

/**
 * Response from GET /api/ingredients/:id/vendors
 */
export interface IngredientVendorsResponse {
  ingredientVendors: IngredientVendor[];
}

/**
 * Request body for POST /api/ingredients/:id/vendors
 */
export interface AddIngredientVendorRequest {
  vendorId: number;
  isPreferred?: boolean;
}

/**
 * Request body for PUT /api/ingredients/:id/vendors/:vendorId
 */
export interface UpdateIngredientVendorRequest {
  isPreferred?: boolean;
}

/**
 * Response from POST /api/ingredients/:id/vendors and PUT /api/ingredients/:id/vendors/:vendorId
 */
export interface IngredientVendorResponse {
  ingredientVendor: IngredientVendor;
}

// =============================================================================
// Procurement API Types
// =============================================================================

export const UNASSIGNED_VENDOR_LABEL = 'Unassigned vendor';

/** RECIPE = derived from menus/RSVPs; MANUAL = added by hand for one week;
 *  STAPLE = RSVP-independent weekly staple (Ingredient.isStaple) whose
 *  calculatedQuantity is the target stock level. */
export type ProcurementItemSource = 'RECIPE' | 'MANUAL' | 'STAPLE';

export interface ProcurementBreakdownEntry {
  menuId: number;
  menuDate: string;
  menuTitle: string;
  recipeId: number | null;
  recipeName: string | null;
  menuRecipeId: number | null;
  isOptional: boolean;
  quantityPerLarge: string;
  sizeCounts?: Partial<RSVPSizeCounts>;
  sizeScaleRatios?: Partial<RSVPScaleRatios>;
  sizeOptOutCounts?: Partial<RSVPSizeCounts>;
  mediumToLargeRatio?: string;
  smallCount?: number;
  mediumCount: number;
  largeCount: number;
  smallOptOutCount?: number;
  mediumOptOutCount?: number;
  largeOptOutCount?: number;
  totalQuantity: string;
  unit: string;
}

export type ProcurementWarningCode =
  | 'MISSING_RECIPE'
  | 'INACTIVE_INGREDIENT'
  | 'MISSING_QUANTITY'
  | 'INVALID_QUANTITY'
  | 'ZERO_QUANTITY'
  | 'UNKNOWN_UNIT'
  | 'MIXED_UNITS';

export interface ProcurementWarning {
  code: ProcurementWarningCode;
  message: string;
  menuId?: number;
  ingredientId?: number;
}

export const QUANTITY_OUT_OF_RANGE = 'QUANTITY_OUT_OF_RANGE';

export type QuantityProblemReason = 'PER_SERVING' | 'WEEKLY_TOTAL';

export interface QuantityProblem {
  reason: QuantityProblemReason;
  ingredientId: number;
  ingredientName: string;
  /** null for a weekly staple */
  recipeId: number | null;
  /** "Weekly staple" for a staple */
  recipeName: string;
  /** YYYY-MM-DD, null for a staple */
  menuDate: string | null;
  /** Decimal string as stored (staple: its target) */
  quantityPerLarge: string;
  unit: string;
  /** This row's scaled total for the week (staple: its purchase) */
  weeklyQuantity: string;
}

export interface ProcurementItem {
  id: number;
  procurementListId: number;
  sourceType: ProcurementItemSource;
  ingredientId: number;
  displayName: string;
  unit: string;
  calculatedQuantity: string;
  purchaseQuantity: string;
  vendorId: number | null;
  vendorNameSnapshot: string | null;
  notes: string | null;
  isExcluded: boolean;
  displayOrder: number;
  calculationBreakdown: ProcurementBreakdownEntry[];
  inventoryQuantity: string | null;
  /** Unit the shared on-hand was saved in (null when none is saved). */
  inventoryUnit?: string | null;
  createdAt: string;
  updatedAt: string;
  ingredient?: Ingredient;
  vendor?: Vendor;
}

export interface ProcurementList {
  id: number;
  weekStartDate: string;
  weekEndDate: string;
  rsvpCountS: number;
  rsvpCountM: number;
  rsvpCountL: number;
  warnings: ProcurementWarning[];
  createdById: string;
  updatedById: string | null;
  generatedAt: string;
  createdAt: string;
  updatedAt: string;
  items: ProcurementItem[];
}

export interface ProcurementListResponse {
  weekStartDate: string;
  weekEndDate: string;
  cutoffPassed: boolean;
  procurementList: ProcurementList | null;
}

export interface GenerateProcurementRequest {
  weekStartDate: string;
}

export interface ProcurementItemResponse {
  procurementItem: ProcurementItem;
}

export interface AddManualProcurementItemRequest {
  ingredientId: number;
  purchaseQuantity: number;
  vendorId?: number | null;
  notes?: string | null;
}

export interface UpdateProcurementItemRequest {
  purchaseQuantity?: number;
  vendorId?: number | null;
  notes?: string | null;
  isExcluded?: boolean;
}

export interface BulkInventoryEntry {
  ingredientId: number;
  quantityOnHand: number | null;
}

export interface BulkInventoryRequest {
  entries: BulkInventoryEntry[];
}

export interface InventoryEntry {
  id: number;
  procurementListId: number;
  ingredientId: number;
  quantityOnHand: string;
  unit: string;
  notes: string | null;
  enteredById: string;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface BulkInventoryResponse {
  entries: InventoryEntry[];
}
